import { lazy, Suspense, useEffect } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import logoMark from '@/images/logos/logo-mark.svg';
import { accentPair, CORNER_RADIUS } from '@shared/theme';
import { Header } from '@/components/Header';
import { Nib } from '@/components/Nib';
import { Button } from '@/components/ui/button';
import { useSession } from '@/lib/session';
import { cn } from '@/lib/utils';
import { AuthorPage } from '@/pages/AuthorPage';
import { Home } from '@/pages/Home';
import { LanguageToggle } from '@/components/LanguageToggle';
import { PostView } from '@/pages/PostView';
import { t } from '@/lib/i18n';

/*
 * Reading is the common case and writing is the rare one, so the writer bundle
 * — TipTap, its ProseMirror deps, the domain settings — is split out. A reader
 * on a slow Nepali mobile connection should never download the editor.
 */
const Dashboard = lazy(() => import('@/pages/Dashboard').then((m) => ({ default: m.Dashboard })));
const PostEditor = lazy(() => import('@/pages/PostEditor').then((m) => ({ default: m.PostEditor })));
const Profile = lazy(() => import('@/pages/Profile').then((m) => ({ default: m.Profile })));
const DomainSettings = lazy(() =>
  import('@/pages/DomainSettings').then((m) => ({ default: m.DomainSettings })),
);
const Login = lazy(() => import('@/pages/Login').then((m) => ({ default: m.Login })));
const Register = lazy(() => import('@/pages/Register').then((m) => ({ default: m.Register })));
const Join = lazy(() => import('@/pages/Join').then((m) => ({ default: m.Join })));
const ForgotPassword = lazy(() =>
  import('@/pages/ForgotPassword').then((m) => ({ default: m.ForgotPassword })),
);
const ResetPassword = lazy(() =>
  import('@/pages/ResetPassword').then((m) => ({ default: m.ResetPassword })),
);
const VerifyEmail = lazy(() => import('@/pages/VerifyEmail').then((m) => ({ default: m.VerifyEmail })));
const Analytics = lazy(() => import('@/pages/Analytics').then((m) => ({ default: m.Analytics })));
const CommentsInbox = lazy(() =>
  import('@/pages/CommentsInbox').then((m) => ({ default: m.CommentsInbox })),
);
const ThemeSettings = lazy(() => import('@/pages/ThemeSettings').then((m) => ({ default: m.ThemeSettings })));

// The admin portal is a separate chunk: most sessions are writers who should
// never pay to download it.
const AdminShell = lazy(() => import('@/pages/admin/AdminShell').then((m) => ({ default: m.AdminShell })));
const AdminOverview = lazy(() => import('@/pages/admin/AdminOverview').then((m) => ({ default: m.AdminOverview })));
const AdminWriters = lazy(() => import('@/pages/admin/AdminWriters').then((m) => ({ default: m.AdminWriters })));
const AdminInvites = lazy(() => import('@/pages/admin/AdminInvites').then((m) => ({ default: m.AdminInvites })));
const AdminTemplates = lazy(() =>
  import('@/pages/admin/AdminTemplates').then((m) => ({ default: m.AdminTemplates })),
);
const AdminPicks = lazy(() => import('@/pages/admin/AdminPicks').then((m) => ({ default: m.AdminPicks })));
const ToolHome = lazy(() => import('@/pages/tool/ToolHome').then((m) => ({ default: m.ToolHome })));
const ToolDocs = lazy(() => import('@/pages/tool/ToolDocs').then((m) => ({ default: m.ToolDocs })));
const ToolKeys = lazy(() => import('@/pages/tool/ToolKeys').then((m) => ({ default: m.ToolKeys })));
const ToolPrivacy = lazy(() => import('@/pages/tool/ToolPrivacy').then((m) => ({ default: m.ToolPrivacy })));

/**
 * One route table for both site shapes.
 *
 * On a tenant host a post lives at /p/:slug (the author is the Host header); on
 * the platform it lives at /@handle/:slug. Both paths are always registered, so
 * a link that leaks across hosts still resolves.
 */
export function App() {
  const { loading, site } = useSession();

  /*
   * The site's theme is applied by stamping tokens onto <html> — data attributes
   * for the discrete choices, custom properties for the accent and the corner
   * radius — so every component picks it up without knowing themes exist.
   *
   * The accent is published as `--tenant-accent`, not as `--primary` directly.
   * An inline style beats any stylesheet rule, so writing `--primary` here also
   * overwrote the lighter accent `.dark` ships, and a writer's own site rendered
   * its links and buttons in a paper-weight colour against a near-black page.
   * Publishing the pair and letting index.css choose between them puts the
   * decision back where the reader's light/sepia/dark choice can win.
   */
  useEffect(() => {
    const root = document.documentElement;
    const template = site?.template;

    if (!template) {
      root.removeAttribute('data-themed');
      root.style.removeProperty('--tenant-accent');
      root.style.removeProperty('--tenant-accent-dark');
      root.style.removeProperty('--radius');
      return;
    }

    const accent = accentPair(template.accent);
    root.style.setProperty('--tenant-accent', accent.light);
    root.style.setProperty('--tenant-accent-dark', accent.dark);
    root.style.setProperty('--radius', CORNER_RADIUS[template.corners]);

    root.dataset.themed = '';
    root.dataset.layout = template.layout;
    root.dataset.bodyFont = template.bodyFont;
    root.dataset.headingFont = template.headingFont;
    root.dataset.density = template.density;
    root.dataset.paper = template.paper;
    root.dataset.ornament = template.ornament;
    root.dataset.covers = template.showCovers ? 'on' : 'off';
  }, [site?.template]);

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4">
        {/* The first thing anyone sees on a cold load is the mark, not a spinner. */}
        <img src={logoMark} alt="" className="h-14 w-auto animate-pulse opacity-90" />
        <Nib className="h-3 w-auto animate-pulse text-muted-foreground" />
      </div>
    );
  }

  /*
   * typeinnepali.com is a different product on the same Worker.
   *
   * Not a route inside the platform: a visitor there is looking for a typing
   * tool, and a masthead about Nepali literature, a feed of poems and an account
   * menu would all be answers to a question they did not ask. What the two share
   * is everything underneath — the engine, and the one table of what people
   * meant, which is the reason not to split them into two deployments.
   */
  if (site?.kind === 'tool') return <ToolSite />;

  return (
    <div className="flex min-h-screen flex-col">
      <Header />

      <main className="flex-1">
        <Suspense
          fallback={
            <div className="flex justify-center py-24">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </div>
          }
        >
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/p/:slug" element={<PostView />} />
            <Route path="/about" element={<AuthorPage />} />

            {/*
              The same landing page the tool's own domain serves — not a second
              copy of it. This URL already declares typeinnepali.com canonical,
              and two pages saying the same thing is what that canonical exists
              to prevent.
            */}
            <Route path="/nepali-typing" element={<ToolHome />} />

            {/* Canonical author URLs. */}
            <Route path="/author/:handle" element={<AuthorPage />} />
            <Route path="/author/:handle/:slug" element={<PostView />} />

            {/*
              Legacy alias for /@handle. Two things make this necessary rather
              than decorative: Cloudflare's asset router rewrites "@" to "%40"
              before we ever see the request, and React Router v6 only turns
              ":param" into a capture group when it directly follows a slash, so
              a literal "/@:handle" route matches nothing at all. The "@" lives
              inside the param here, and anything that reaches it is redirected
              to the canonical path.
            */}
            <Route path="/:handle" element={<AtHandleRedirect />} />
            <Route path="/:handle/:slug" element={<AtHandleRedirect />} />

            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/join/:code" element={<Join />} />
            <Route path="/forgot" element={<ForgotPassword />} />
            <Route path="/reset" element={<ResetPassword />} />
            <Route path="/verify" element={<VerifyEmail />} />

            <Route
              path="/dashboard"
              element={
                <RequireAuth>
                  <Dashboard />
                </RequireAuth>
              }
            />
            <Route
              path="/dashboard/new"
              element={
                <RequireAuth>
                  <PostEditor />
                </RequireAuth>
              }
            />
            <Route
              path="/dashboard/p/:id"
              element={
                <RequireAuth>
                  <PostEditor />
                </RequireAuth>
              }
            />
            <Route
              path="/dashboard/analytics"
              element={
                <RequireAuth>
                  <Analytics />
                </RequireAuth>
              }
            />
            <Route
              path="/dashboard/comments"
              element={
                <RequireAuth>
                  <CommentsInbox />
                </RequireAuth>
              }
            />
            <Route
              path="/dashboard/profile"
              element={
                <RequireAuth>
                  <Profile />
                </RequireAuth>
              }
            />
            <Route
              path="/dashboard/theme"
              element={
                <RequireAuth>
                  <ThemeSettings />
                </RequireAuth>
              }
            />
            <Route
              path="/dashboard/domain"
              element={
                <RequireAuth>
                  <DomainSettings />
                </RequireAuth>
              }
            />

            <Route
              path="/admin"
              element={
                <RequireAdmin>
                  <AdminShell />
                </RequireAdmin>
              }
            >
              <Route index element={<AdminOverview />} />
              <Route path="writers" element={<AdminWriters />} />
              <Route path="invites" element={<AdminInvites />} />
              <Route path="templates" element={<AdminTemplates />} />
              <Route path="picks" element={<AdminPicks />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>

      <Footer />
    </div>
  );
}

/**
 * The typing tool's own site.
 *
 * Its own shell, deliberately thin: a header that is a wordmark and two links,
 * the tool, the docs, and nothing else. Everything the publishing platform needs
 * — sessions, the feed, the editor — is code this site never reaches.
 */
function ToolSite() {
  const { site } = useSession();

  return (
    <div className="flex min-h-screen flex-col">
      <header className="border-b">
        <div className="container flex h-14 items-center gap-4">
          <Link to="/" className="np text-lg font-semibold">
            {t('नेपाली टाइपिङ')}
          </Link>
          <nav className="ms-auto flex items-center gap-4 text-sm">
            <LanguageToggle />
            <Link to="/docs" className="np text-muted-foreground hover:text-foreground">
              {t('जोड्ने तरिका')}
            </Link>
            <Link to="/keys" className="np text-muted-foreground hover:text-foreground">
              {t('API कुञ्जी')}
            </Link>
          </nav>
        </div>
      </header>

      <main className="flex-1">
        <Suspense
          fallback={
            <div className="flex justify-center py-24">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </div>
          }
        >
          <Routes>
            <Route path="/" element={<ToolHome />} />
            <Route path="/docs" element={<ToolDocs />} />
            <Route path="/keys" element={<ToolKeys />} />
            <Route path="/privacy" element={<ToolPrivacy />} />
            {/* The tool's own address for the tool is "/", so the platform's
                path redirects rather than serving a second copy of it. */}
            <Route path="/nepali-typing" element={<Navigate to="/" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>

      <footer className="mt-16 border-t py-8">
        <div className="container flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
          <span className="np">{t('निःशुल्क, खुला — नेपाली भाषाका लागि।')}</span>
          <nav className="flex flex-wrap items-center gap-4">
            {/* A typing tool reads every keystroke, so where it says what it does
                with them belongs on every page, not only in the docs. */}
            <Link to="/privacy" className="np hover:text-foreground">
              {t('गोपनीयता')}
            </Link>
            <a href={`https://${site?.appHost ?? 'kalampress.com'}`} className="np hover:text-foreground">
              {t('कलम प्रेस')}
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { viewer, loading } = useSession();
  const location = useLocation();

  if (loading) return null;
  if (!viewer) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <>{children}</>;
}

/**
 * Admin gate. The API already 404s these routes for non-admins — this only
 * keeps a writer from seeing an empty shell flash before the requests fail.
 */
function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { viewer, loading } = useSession();
  const location = useLocation();

  if (loading) return null;
  if (!viewer) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (viewer.role !== 'admin') return <NotFound />;
  return <>{children}</>;
}

/**
 * Sends /@handle (and its %40 form) to the canonical /author/handle.
 *
 * These routes are catch-alls, so anything that is not an "@" URL — a typo, a
 * dead link — is a genuine 404 rather than an empty profile page. React Router
 * decodes params before we see them, so one check covers both spellings.
 */
function AtHandleRedirect() {
  const { handle, slug } = useParams<{ handle: string; slug?: string }>();

  if (!handle?.startsWith('@') || handle.length < 2) return <NotFound />;

  const target = `/author/${encodeURIComponent(handle.slice(1))}${slug ? `/${encodeURIComponent(slug)}` : ''}`;
  return <Navigate to={target} replace />;
}

function NotFound() {
  return (
    <div className="container max-w-lg py-24 text-center">
      {/* The mark rather than an emoji: a dead link is still our page. */}
      <img src={logoMark} alt="" className="mx-auto h-16 w-auto opacity-25" />
      <h1 className="headline mt-6 text-3xl">{t('यो पृष्ठ भेटिएन')}</h1>
      <p className="np mt-2 text-muted-foreground">{t('लिंक पुरानो वा गलत हुन सक्छ।')}</p>
      <Button className="mt-6" asChild>
        <Link to="/">{t('गृहपृष्ठ')}</Link>
      </Button>
    </div>
  );
}

function Footer() {
  const { site } = useSession();
  const tenant = site?.tenant;

  return (
    <footer className={cn('mt-16 py-8', tenant ? 'border-t' : 'border-t-2 border-foreground/80')}>
      <div
        className={cn(
          'flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground',
          tenant ? 'container' : 'broadsheet',
        )}
      >
        <span className="np flex items-center gap-2">
          {!tenant && <Nib className="h-3.5 w-auto text-primary" />}
          {tenant ? `© ${tenant.penName || tenant.displayName}` : `© ${site?.siteName ?? t('कलम प्रेस')}`}
        </span>
        <div className="flex flex-wrap items-center gap-4">
          <a href="/rss.xml" className="hover:text-foreground">
            RSS
          </a>
          {/*
            A real link, on every page of the platform. The tool is the thing
            most people arrive looking for, and a page reachable only by typing
            its address is a page search engines have no reason to trust.
          */}
          {!tenant && (
            <Link to="/nepali-typing" className="np hover:text-foreground">{t('नेपाली टाइपिङ')}</Link>
          )}
          {tenant ? (
            <a href={`https://${site?.appHost}`} className="np hover:text-foreground">{t('कलम प्रेसमा बनेको')}</a>
          ) : (
            <Link to="/register" className="np hover:text-foreground">{t('आफ्नो साइट सुरु गर्नुहोस्')}</Link>
          )}
        </div>
      </div>
    </footer>
  );
}
